// ClaimTracker data service — all DB operations using ct_ prefixed tables
import { createClient } from '@/lib/supabase/client';
import type { ClaimItem, ClaimSettings, FamilyMember, Room, Claim } from '@/lib/claimStore';

// ─── helpers ────────────────────────────────────────────────────────────────

function rowToClaimItem(row: any): ClaimItem {
  return {
    id: row.id,
    name: row.name ?? '',
    description: row.description ?? '',
    quantity: row.quantity ?? 1,
    location: row.location ?? '',
    addedBy: row.added_by ?? '',
    condition: row.condition ?? 'Destroyed',
    estimatedAge: row.estimated_age ?? '',
    notes: row.notes ?? '',
    priceStatus: row.price_status ?? 'unpriced',
    unitPrice: parseFloat(row.unit_price ?? '0'),
    priceResearch:
      row.research_source_name
        ? {
            price: parseFloat(row.research_price ?? '0'),
            sourceName: row.research_source_name ?? '',
            sourceUrl: row.research_source_url ?? '',
            researchDate: row.research_date ?? '',
            confidence: row.research_confidence ?? 'medium',
            notes: row.research_notes ?? '',
          }
        : null,
    dateAdded: row.date_added ?? '',
    inputMethod: row.input_method ?? 'typed',
    category: row.category ?? '',
  };
}

function claimItemToRow(item: Partial<ClaimItem> & { name: string }) {
  const row: any = {
    name: item.name,
    description: item.description ?? '',
    quantity: item.quantity ?? 1,
    location: item.location ?? '',
    added_by: item.addedBy ?? '',
    condition: item.condition ?? 'Destroyed',
    estimated_age: item.estimatedAge ?? '',
    notes: item.notes ?? '',
    price_status: item.priceStatus ?? 'unpriced',
    unit_price: item.unitPrice ?? 0,
    date_added: item.dateAdded ?? new Date().toISOString().split('T')[0],
    input_method: item.inputMethod ?? 'typed',
    category: item.category ?? '',
  };
  if (item.priceResearch) {
    row.research_price = item.priceResearch.price;
    row.research_source_name = item.priceResearch.sourceName;
    row.research_source_url = item.priceResearch.sourceUrl;
    row.research_date = item.priceResearch.researchDate;
    row.research_confidence = item.priceResearch.confidence;
    row.research_notes = item.priceResearch.notes;
  } else {
    row.research_price = null;
    row.research_source_name = null;
    row.research_source_url = null;
    row.research_date = null;
    row.research_confidence = null;
    row.research_notes = null;
  }
  return row;
}

function rowToSettings(row: any): ClaimSettings {
  return {
    policyNumber: row.policy_number ?? '',
    insuranceCompany: row.insurance_company ?? '',
    adjusterName: row.adjuster_name ?? '',
    adjusterPhone: row.adjuster_phone ?? '',
    adjusterEmail: row.adjuster_email ?? '',
    dateOfLoss: row.date_of_loss ?? '',
    propertyAddress: row.property_address ?? '',
    claimNumber: row.claim_number ?? '',
  };
}

function rowToClaim(row: any): Claim {
  return {
    id: row.id,
    name: row.name ?? '',
    policyNumber: row.policy_number ?? '',
    insuranceCompany: row.insurance_company ?? '',
    adjusterName: row.adjuster_name ?? '',
    adjusterPhone: row.adjuster_phone ?? '',
    adjusterEmail: row.adjuster_email ?? '',
    dateOfLoss: row.date_of_loss ?? '',
    propertyAddress: row.property_address ?? '',
    claimNumber: row.claim_number ?? '',
    isActive: row.is_active ?? false,
    createdAt: row.created_at ?? '',
  };
}

// ─── claims ──────────────────────────────────────────────────────────────────

export type { Claim };

export async function fetchClaims(): Promise<Claim[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_claims')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) { console.error('fetchClaims:', error.message); return []; }
  return (data ?? []).map(rowToClaim);
}

export async function fetchActiveClaim(): Promise<Claim | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_claims')
    .select('*')
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();
  if (error) { console.error('fetchActiveClaim:', error.message); return null; }
  return data ? rowToClaim(data) : null;
}

export async function createClaim(name: string): Promise<Claim | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_claims')
    .insert({ name, is_active: false })
    .select()
    .single();
  if (error) { console.error('createClaim:', error.message); return null; }
  return rowToClaim(data);
}

export async function updateClaim(id: string, updates: Partial<Omit<Claim, 'id' | 'createdAt'>>): Promise<boolean> {
  const supabase = createClient();
  const row: any = {};
  if (updates.name !== undefined) row.name = updates.name;
  if (updates.policyNumber !== undefined) row.policy_number = updates.policyNumber;
  if (updates.insuranceCompany !== undefined) row.insurance_company = updates.insuranceCompany;
  if (updates.adjusterName !== undefined) row.adjuster_name = updates.adjusterName;
  if (updates.adjusterPhone !== undefined) row.adjuster_phone = updates.adjusterPhone;
  if (updates.adjusterEmail !== undefined) row.adjuster_email = updates.adjusterEmail;
  if (updates.dateOfLoss !== undefined) row.date_of_loss = updates.dateOfLoss;
  if (updates.propertyAddress !== undefined) row.property_address = updates.propertyAddress;
  if (updates.claimNumber !== undefined) row.claim_number = updates.claimNumber;
  const { error } = await supabase.from('ct_claims').update(row).eq('id', id);
  if (error) { console.error('updateClaim:', error.message); return false; }
  return true;
}

export async function setActiveClaim(id: string): Promise<boolean> {
  const supabase = createClient();
  // Deactivate all
  await supabase.from('ct_claims').update({ is_active: false }).neq('id', '00000000-0000-0000-0000-000000000000');
  // Activate selected
  const { error } = await supabase.from('ct_claims').update({ is_active: true }).eq('id', id);
  if (error) { console.error('setActiveClaim:', error.message); return false; }
  return true;
}

export async function deleteClaim(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_claims').delete().eq('id', id);
  if (error) { console.error('deleteClaim:', error.message); return false; }
  return true;
}

// ─── claim items ────────────────────────────────────────────────────────────

export async function fetchClaimItems(claimId?: string): Promise<ClaimItem[]> {
  const supabase = createClient();
  let query = supabase
    .from('ct_claim_items')
    .select('*')
    .order('created_at', { ascending: false });
  if (claimId) {
    query = query.eq('claim_id', claimId);
  }
  const { data, error } = await query;
  if (error) { console.error('fetchClaimItems:', error.message); return []; }
  return (data ?? []).map(rowToClaimItem);
}

export async function insertClaimItem(item: Omit<ClaimItem, 'id'>, claimId?: string): Promise<ClaimItem | null> {
  const supabase = createClient();
  const row = claimItemToRow(item as any);
  if (claimId) row.claim_id = claimId;
  const { data, error } = await supabase
    .from('ct_claim_items')
    .insert(row)
    .select()
    .single();
  if (error) { console.error('insertClaimItem:', error.message); return null; }
  return rowToClaimItem(data);
}

export async function updateClaimItem(id: string, updates: Partial<ClaimItem>): Promise<ClaimItem | null> {
  const supabase = createClient();
  // Build a sparse row — only include fields that are explicitly provided in updates
  const row: any = {};
  if (updates.name !== undefined)        row.name = updates.name;
  if (updates.description !== undefined) row.description = updates.description;
  if (updates.quantity !== undefined)    row.quantity = updates.quantity;
  if (updates.location !== undefined)    row.location = updates.location;
  if (updates.addedBy !== undefined)     row.added_by = updates.addedBy;
  if (updates.condition !== undefined)   row.condition = updates.condition;
  if (updates.estimatedAge !== undefined) row.estimated_age = updates.estimatedAge;
  if (updates.notes !== undefined)       row.notes = updates.notes;
  if (updates.priceStatus !== undefined) row.price_status = updates.priceStatus;
  if (updates.unitPrice !== undefined)   row.unit_price = updates.unitPrice;
  if (updates.dateAdded !== undefined)   row.date_added = updates.dateAdded;
  if (updates.inputMethod !== undefined) row.input_method = updates.inputMethod;
  if (updates.category !== undefined)    row.category = updates.category;
  if (updates.priceResearch !== undefined) {
    if (updates.priceResearch) {
      row.research_price = updates.priceResearch.price;
      row.research_source_name = updates.priceResearch.sourceName;
      row.research_source_url = updates.priceResearch.sourceUrl;
      row.research_date = updates.priceResearch.researchDate;
      row.research_confidence = updates.priceResearch.confidence;
      row.research_notes = updates.priceResearch.notes;
    } else {
      row.research_price = null;
      row.research_source_name = null;
      row.research_source_url = null;
      row.research_date = null;
      row.research_confidence = null;
      row.research_notes = null;
    }
  }
  const { data, error } = await supabase
    .from('ct_claim_items')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error) { console.error('updateClaimItem:', error.message); return null; }
  return rowToClaimItem(data);
}

export async function deleteClaimItem(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_claim_items').delete().eq('id', id);
  if (error) { console.error('deleteClaimItem:', error.message); return false; }
  return true;
}

export async function deleteAllClaimItems(claimId?: string): Promise<boolean> {
  const supabase = createClient();
  let query = supabase.from('ct_claim_items').delete();
  if (claimId) {
    query = query.eq('claim_id', claimId);
  } else {
    query = query.neq('id', '00000000-0000-0000-0000-000000000000');
  }
  const { error } = await query;
  if (error) { console.error('deleteAllClaimItems:', error.message); return false; }
  return true;
}

// ─── claim settings ─────────────────────────────────────────────────────────

export async function fetchClaimSettings(): Promise<ClaimSettings | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_claim_settings')
    .select('*')
    .limit(1)
    .maybeSingle();
  if (error) { console.error('fetchClaimSettings:', error.message); return null; }
  return data ? rowToSettings(data) : null;
}

export async function upsertClaimSettings(settings: ClaimSettings): Promise<boolean> {
  const supabase = createClient();
  const { data: existing } = await supabase
    .from('ct_claim_settings')
    .select('id')
    .limit(1)
    .maybeSingle();

  const row = {
    policy_number: settings.policyNumber,
    insurance_company: settings.insuranceCompany,
    adjuster_name: settings.adjusterName,
    adjuster_phone: settings.adjusterPhone,
    adjuster_email: settings.adjusterEmail,
    date_of_loss: settings.dateOfLoss,
    property_address: settings.propertyAddress,
    claim_number: settings.claimNumber,
  };

  if (existing?.id) {
    const { error } = await supabase.from('ct_claim_settings').update(row).eq('id', existing.id);
    if (error) { console.error('upsertClaimSettings update:', error.message); return false; }
  } else {
    const { error } = await supabase.from('ct_claim_settings').insert(row);
    if (error) { console.error('upsertClaimSettings insert:', error.message); return false; }
  }
  return true;
}

// ─── family members ─────────────────────────────────────────────────────────

export async function fetchFamilyMembers(claimId?: string): Promise<FamilyMember[]> {
  const supabase = createClient();
  let query = supabase
    .from('ct_family_members')
    .select('*')
    .order('sort_order', { ascending: true });
  if (claimId) {
    query = query.eq('claim_id', claimId);
  }
  const { data, error } = await query;
  if (error) { console.error('fetchFamilyMembers:', error.message); return []; }
  return (data ?? []).map((r: any) => ({ id: r.id, name: r.name, color: r.color }));
}

export async function insertFamilyMember(member: Omit<FamilyMember, 'id'>, claimId?: string): Promise<FamilyMember | null> {
  const supabase = createClient();
  const { data: existing } = await supabase
    .from('ct_family_members')
    .select('sort_order')
    .eq('claim_id', claimId ?? '')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const sortOrder = (existing?.sort_order ?? 0) + 1;
  const row: any = { name: member.name, color: member.color, sort_order: sortOrder };
  if (claimId) row.claim_id = claimId;
  const { data, error } = await supabase
    .from('ct_family_members')
    .insert(row)
    .select()
    .single();
  if (error) { console.error('insertFamilyMember:', error.message); return null; }
  return { id: data.id, name: data.name, color: data.color };
}

export async function updateFamilyMember(id: string, updates: Partial<FamilyMember>): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('ct_family_members')
    .update({ name: updates.name, color: updates.color })
    .eq('id', id);
  if (error) { console.error('updateFamilyMember:', error.message); return false; }
  return true;
}

export async function deleteFamilyMember(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_family_members').delete().eq('id', id);
  if (error) { console.error('deleteFamilyMember:', error.message); return false; }
  return true;
}

// ─── rooms ───────────────────────────────────────────────────────────────────

export async function fetchRooms(claimId?: string): Promise<Room[]> {
  const supabase = createClient();
  let query = supabase
    .from('ct_rooms')
    .select('*')
    .order('sort_order', { ascending: true });
  if (claimId) {
    query = query.eq('claim_id', claimId);
  }
  const { data, error } = await query;
  if (error) { console.error('fetchRooms:', error.message); return []; }
  return (data ?? []).map((r: any) => ({ id: r.id, name: r.name }));
}

export async function insertRoom(name: string, claimId?: string): Promise<Room | null> {
  const supabase = createClient();
  let sortQuery = supabase.from('ct_rooms').select('sort_order').order('sort_order', { ascending: false }).limit(1);
  if (claimId) sortQuery = sortQuery.eq('claim_id', claimId);
  const { data: existing } = await sortQuery.maybeSingle();
  const sortOrder = (existing?.sort_order ?? 0) + 1;
  const row: any = { name, sort_order: sortOrder };
  if (claimId) row.claim_id = claimId;
  const { data, error } = await supabase
    .from('ct_rooms')
    .insert(row)
    .select()
    .single();
  if (error) { console.error('insertRoom:', error.message); return null; }
  return { id: data.id, name: data.name };
}

export async function deleteRoom(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_rooms').delete().eq('id', id);
  if (error) { console.error('deleteRoom:', error.message); return false; }
  return true;
}

export async function updateRoom(id: string, name: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_rooms').update({ name }).eq('id', id);
  if (error) { console.error('updateRoom:', error.message); return false; }
  return true;
}

// ─── item revisions ──────────────────────────────────────────────────────────

export interface ItemRevision {
  id: string;
  itemId: string;
  price: number;
  source: string;
  note: string;
  date: string;
}

export interface ItemNote {
  id: string;
  itemId: string;
  text: string;
  date: string;
}

export async function fetchItemRevisions(itemId: string): Promise<ItemRevision[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_item_revisions')
    .select('*')
    .eq('item_id', itemId)
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchItemRevisions:', error.message); return []; }
  return (data ?? []).map((r: any) => ({
    id: r.id,
    itemId: r.item_id,
    price: parseFloat(r.price ?? '0'),
    source: r.source ?? '',
    note: r.note ?? '',
    date: r.revision_date ?? '',
  }));
}

export async function insertItemRevision(
  itemId: string,
  revision: { price: number; source: string; note: string; date: string }
): Promise<ItemRevision | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_item_revisions')
    .insert({
      item_id: itemId,
      price: revision.price,
      source: revision.source,
      note: revision.note,
      revision_date: revision.date,
    })
    .select()
    .single();
  if (error) { console.error('insertItemRevision:', error.message); return null; }
  return {
    id: data.id,
    itemId: data.item_id,
    price: parseFloat(data.price ?? '0'),
    source: data.source ?? '',
    note: data.note ?? '',
    date: data.revision_date ?? '',
  };
}

export async function deleteItemRevision(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_item_revisions').delete().eq('id', id);
  if (error) { console.error('deleteItemRevision:', error.message); return false; }
  return true;
}

// ─── item notes ──────────────────────────────────────────────────────────────

export async function fetchItemNotes(itemId: string): Promise<ItemNote[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_item_notes')
    .select('*')
    .eq('item_id', itemId)
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchItemNotes:', error.message); return []; }
  return (data ?? []).map((r: any) => ({
    id: r.id,
    itemId: r.item_id,
    text: r.text ?? '',
    date: r.note_date ?? '',
  }));
}

export async function insertItemNote(
  itemId: string,
  note: { text: string; date: string }
): Promise<ItemNote | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_item_notes')
    .insert({
      item_id: itemId,
      text: note.text,
      note_date: note.date,
    })
    .select()
    .single();
  if (error) { console.error('insertItemNote:', error.message); return null; }
  return {
    id: data.id,
    itemId: data.item_id,
    text: data.text ?? '',
    date: data.note_date ?? '',
  };
}

export async function deleteItemNote(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_item_notes').delete().eq('id', id);
  if (error) { console.error('deleteItemNote:', error.message); return false; }
  return true;
}

// ─── claim categories ────────────────────────────────────────────────────────

export interface ClaimCategory {
  id: string;
  claimId: string;
  name: string;
  sortOrder: number;
}

export async function fetchClaimCategories(claimId: string): Promise<ClaimCategory[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ct_claim_categories')
    .select('*')
    .eq('claim_id', claimId)
    .order('sort_order', { ascending: true });
  if (error) { console.error('fetchClaimCategories:', error.message); return []; }
  return (data ?? []).map((r: any) => ({
    id: r.id,
    claimId: r.claim_id,
    name: r.name,
    sortOrder: r.sort_order,
  }));
}

export async function insertClaimCategory(claimId: string, name: string): Promise<ClaimCategory | null> {
  const supabase = createClient();
  const { data: existing } = await supabase
    .from('ct_claim_categories')
    .select('sort_order')
    .eq('claim_id', claimId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const sortOrder = (existing?.sort_order ?? 0) + 1;
  const { data, error } = await supabase
    .from('ct_claim_categories')
    .insert({ claim_id: claimId, name: name.trim(), sort_order: sortOrder })
    .select()
    .single();
  if (error) { console.error('insertClaimCategory:', error.message); return null; }
  return { id: data.id, claimId: data.claim_id, name: data.name, sortOrder: data.sort_order };
}

export async function updateClaimCategory(id: string, name: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('ct_claim_categories')
    .update({ name: name.trim() })
    .eq('id', id);
  if (error) { console.error('updateClaimCategory:', error.message); return false; }
  return true;
}

export async function deleteClaimCategory(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('ct_claim_categories').delete().eq('id', id);
  if (error) { console.error('deleteClaimCategory:', error.message); return false; }
  return true;
}
